// A member's page, `people/<provider>/<claim>/<value>` — the three parts that
// key their row (01-the-console.md § the address; § the moves, page by page:
// People; decision 0069): their role and how they sign in, their invite while
// they wait, their identifier, quiet, and their recent sign-ins as a list,
// each row the record's facts — there is no page for another person's
// sign-in record. *Disconnect* is the page's primary action once they are in;
// *Send again* is in the page's menu while they wait. Changing the role and
// removing the row stay on the list's row menu.

import type { ReactNode } from "react";
import { Link, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import type { TenantProfile } from "../../platform/profile.ts";
import { Empty, NotAllowed, Notice, PageHead, Pending, Pill, useLoad, when } from "../../ui/components.tsx";
import { Ident } from "../../ui/ident.tsx";
import { RowMenu } from "../../ui/table.tsx";
import { Subjects, type Row, type RowsListReply } from "../../wire/auth.ts";
import { Role, SERVICE } from "../../wire/identity.ts";
import { RecentSignIns } from "../records/SignInRecords.tsx";
import { useSurface } from "../surface.tsx";
import { AskedButton } from "../apps/asked.tsx";
import { usePeopleActs, useSigning } from "./acts.tsx";
import { inviteLine } from "./invite.ts";
import { personName } from "./rows.ts";

export function MemberPage({ tenant }: { tenant: TenantProfile }) {
  // The router hands each segment decoded; the page compares them as the row holds them.
  const { provider = "", claim = "", value = "" } = useParams();
  const s = useSurface();
  const [rows, reload] = useLoad("rows", () =>
    s.bus.call<RowsListReply>(SERVICE, Subjects.rowsList(), undefined),
  );
  const row =
    rows.state === "ready"
      ? rows.value.rows.find((r) => r.provider === provider && r.claim === claim && r.value === value)
      : undefined;
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "people")}>People</Link>
      <span>/</span>
      <span>{row === undefined ? value : personName(row)}</span>
    </>
  );
  if (rows.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading who belongs" />
      </>
    );
  }
  if (rows.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={value} />
        <NotAllowed err={rows.err} what="People" />
      </>
    );
  }
  if (row === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title="Nobody by this name" />
        <Empty>No member of {s.name} is kept under this sign-in.</Empty>
      </>
    );
  }
  return <One row={row} crumb={crumb} tenant={tenant} reload={reload} />;
}

function One({
  row,
  crumb,
  tenant,
  reload,
}: {
  row: Row;
  crumb: ReactNode;
  tenant: TenantProfile;
  reload: () => void;
}) {
  const s = useSurface();
  const signing = useSigning(s, tenant);
  const acts = usePeopleActs(s, reload);
  const entries = acts.memberMenu(row).filter((e) => e.danger !== true);
  const disconnect = entries.find((e) => e.label === "Disconnect");
  const rest = entries.filter((e) => e !== disconnect);
  const invite = inviteLine(row);
  const handle = row.display !== undefined && row.display !== "" && row.display !== row.value;
  return (
    <>
      <PageHead
        crumb={crumb}
        title={personName(row)}
        line={
          row.pending === true
            ? `Waiting for their first sign-in, as ${row.role === Role.admin ? "an admin" : "a member"}.`
            : `${row.role === Role.admin ? "An admin" : "A member"} of ${s.name}.`
        }
        action={
          <>
            {disconnect !== undefined && <AskedButton entry={disconnect} />}
            <RowMenu label={`More for ${personName(row)}`} entries={rest} />
          </>
        }
      />
      {acts.err !== undefined && <NotAllowed err={acts.err} />}
      {acts.note !== undefined && <Notice kind="ok">{acts.note}</Notice>}
      <section className="section">
        <div className="card">
          <dl className="kv">
            <dt>Role</dt>
            <dd>
              <Pill strong={row.role === Role.admin}>{row.role}</Pill>
              {row.pending === true && <Pill>waiting</Pill>}
            </dd>
            <dt>Signs in with</dt>
            <dd>{signing.signsInWith(row)}</dd>
            {row.pending === true ? (
              <>
                <dt>Added</dt>
                <dd>{when(row.written)}</dd>
                {invite !== undefined && (
                  <>
                    <dt>Invite</dt>
                    <dd>{invite.text}</dd>
                  </>
                )}
              </>
            ) : (
              <>
                <dt>Since</dt>
                <dd>{when(row.written)}</dd>
              </>
            )}
            {handle && (
              <>
                <dt>{row.claim === "sub" ? "Subject" : row.claim}</dt>
                <dd>
                  <Ident value={row.value} label="their identifier" />
                </dd>
              </>
            )}
          </dl>
        </div>
      </section>
      {row.pending !== true && (
        <div className="section">
          <div className="section-head">
            <h2>Recent sign-ins</h2>
          </div>
          <RecentSignIns subject={row.value} />
        </div>
      )}
    </>
  );
}
